import { AnnotationType } from "building-types/annotation/annotationTypesEnum";
import type { Doc } from "building-types/doc";
import type { PlanAreaMeasurementShape } from "building-types/entities/measurementTypes";
import type { FloorAssemblyOverride, FloorBoundaryFields } from "building-types/floors/floorAssemblyTypes";
import type {
  DocEntityHandle,
  EdgeHandle,
  FloorEdgeHandle,
  FloorOpeningEdgeHandle,
  ShapeBasedEntityHandle,
  ShapeHandle,
  VertexHandle,
} from "building-types/handles/docEntityHandleTypes";
import { assertUnreachable } from "common/utils/assertUnreachable";
import type { PolyLine2 } from "geom/polyLine2Types";
import { docGetShape } from "./docGetShape";
import { isHorizontalFloorAssemblyFreeForm, isSolidFloorAssemblyOverride } from "./elements/floor/floorAssembly";
import { floorOpeningIndexFromShapeId, floorPerimeterIndexFromShapeId } from "./elements/floor/floorShapeId";
import { isFloorEdgeHandle, isFloorOpeningEdgeHandle, isFoundationEdgeHandle } from "./entity/docEntityEdgeHandleTools";
import { docEntityIsShapeBased } from "./entity/docEntityHandleTools";
import { docGetTableEntity } from "./entity/docGetTableEntity";
import { isEditableCeilingOutline } from "./isEditableCeilingOutline";
import { isVirtualRoofBlock } from "./roofSystemTools";

export function docGetMeasurementShape(
  doc: Doc,
  { parent, shapeId }: ShapeHandle,
): PlanAreaMeasurementShape | undefined {
  if (parent.type !== "MEASUREMENT") {
    return undefined;
  }
  const measurement = docGetTableEntity(doc, parent);
  return measurement?.shapes[shapeId];
}

export function docGetSiteShape(doc: Doc, { parent }: ShapeHandle): ShapeHandle | undefined {
  if (parent.type !== "SITE_SHAPE") {
    return undefined;
  }
  const shape = docGetTableEntity(doc, parent);
  if (shape) {
    return { type: "SHAPE", parent, shapeId: "geom" };
  }
  return undefined;
}

export function parentShapeFromEdgeOrVertexHandle(handle: EdgeHandle | VertexHandle): ShapeHandle {
  return { type: "SHAPE", parent: handle.parent, shapeId: handle.shapeId };
}

/**
 * Resolve an edge/vertex handle to the shape handle that {@link docGetShape} understands.
 * Label-style floor handles use `outline`/`opening` plus an index field; doc shapes use `outline:N`/`opening:N`.
 */
export function resolveShapeHandleFromEdgeOrVertex(handle: EdgeHandle | VertexHandle): ShapeHandle {
  if (isFloorOpeningEdgeHandle(handle)) {
    return {
      type: "SHAPE",
      parent: handle.parent,
      shapeId: `opening:${handle.openingIndex}`,
    };
  }
  if (isFloorEdgeHandle(handle) && "perimeterIndex" in handle && typeof handle.perimeterIndex === "number") {
    return {
      type: "SHAPE",
      parent: handle.parent,
      shapeId: `outline:${handle.perimeterIndex}`,
    };
  }
  if (isFoundationEdgeHandle(handle)) {
    return {
      type: "SHAPE",
      parent: handle.parent,
      shapeId: "outline",
    };
  }
  return parentShapeFromEdgeOrVertexHandle(handle);
}

/**
 * Normalize click-selected floor edge handles (`outline:N` / `opening:N`) to the label form
 * (`outline`+`perimeterIndex` / `opening`+`openingIndex`) used by Move Edges commit.
 */
export function normalizeFloorEdgeHandleForMoveEdges(handle: EdgeHandle): EdgeHandle {
  if (handle.parent.type !== "FLOOR") {
    return handle;
  }

  const perimeterIndex = floorPerimeterIndexFromShapeId(handle.shapeId);
  if (perimeterIndex !== null) {
    const normalized: FloorEdgeHandle = {
      type: "EDGE",
      parent: handle.parent,
      shapeId: "outline",
      edgeId: handle.edgeId,
      perimeterIndex,
      ...("stableEdgeId" in handle && typeof handle.stableEdgeId === "string"
        ? { stableEdgeId: handle.stableEdgeId }
        : {}),
    };
    return normalized;
  }

  const openingIndex = floorOpeningIndexFromShapeId(handle.shapeId);
  if (openingIndex !== null) {
    const normalized: FloorOpeningEdgeHandle = {
      type: "EDGE",
      parent: handle.parent,
      shapeId: "opening",
      edgeId: handle.edgeId,
      openingIndex,
      ...("stableEdgeId" in handle && typeof handle.stableEdgeId === "string"
        ? { stableEdgeId: handle.stableEdgeId }
        : {}),
    };
    return normalized;
  }

  return handle;
}

/** Perimeter index from a floor edge handle (label field or indexed shapeId). */
export function floorPerimeterIndexFromEdgeHandle(handle: EdgeHandle): number | null {
  if ("perimeterIndex" in handle && typeof handle.perimeterIndex === "number") {
    return handle.perimeterIndex;
  }
  return floorPerimeterIndexFromShapeId(handle.shapeId);
}

/** Opening index from a floor opening edge handle (label field or indexed shapeId). */
export function floorOpeningIndexFromEdgeHandle(handle: EdgeHandle): number | null {
  if ("openingIndex" in handle && typeof handle.openingIndex === "number") {
    return handle.openingIndex;
  }
  return floorOpeningIndexFromShapeId(handle.shapeId);
}

/**
 * `FloorBoundaryFields` (`edgeIds`/`perimeterEdgeCounts`/`openingEdgeIds`/`openingEdgeCounts`) is
 * only declared on the free-form variant of `FloorAssemblyOverride`; room-derived floors don't
 * store this data on the doc. Widen access for callers that only care about free-form floors and
 * treat missing fields as empty.
 */
export function getFloorBoundaryFields(floor: FloorAssemblyOverride): {
  readonly edgeIds: string[];
  readonly perimeterEdgeCounts: number[] | undefined;
  readonly openingEdgeIds: string[];
  readonly openingEdgeCounts: number[] | undefined;
} {
  const boundaryFields = floor as Partial<FloorBoundaryFields>;
  return {
    edgeIds: boundaryFields.edgeIds ?? [],
    perimeterEdgeCounts: boundaryFields.perimeterEdgeCounts,
    openingEdgeIds: boundaryFields.openingEdgeIds ?? [],
    openingEdgeCounts: boundaryFields.openingEdgeCounts,
  };
}

/**
 * Extract the edge-ID slice for a single perimeter/opening from a combined `edgeIds` array,
 * given the per-ring edge counts. If `edgeCounts` is undefined the combined array is assumed
 * to belong entirely to index 0. Mirrors `getEdgeIdsForPerimeter` in
 * `building-generation/elements/floor/floor.ts` (duplicated here since `doc` cannot depend on
 * `building-generation`).
 */
export function getEdgeIdsForPerimeter(edgeIds: string[], edgeCounts: number[] | undefined, index: number): string[] {
  if (!edgeCounts) {
    return index === 0 ? edgeIds : [];
  }
  let start = 0;
  for (let i = 0; i < index; i++) {
    start += edgeCounts[i] ?? 0;
  }
  const count = edgeCounts[index] ?? 0;
  return edgeIds.slice(start, start + count);
}

export function docEntityHasEditableShape(doc: Doc, handle: DocEntityHandle): handle is ShapeBasedEntityHandle {
  if (!docEntityIsShapeBased(handle)) {
    return false;
  }
  return docGetShapeHandlesFromShapeBasedEntity(doc, handle).length > 0;
}

export function docGetShapeHandlesFromShapeBasedEntity(doc: Doc, handle: ShapeBasedEntityHandle): ShapeHandle[] {
  switch (handle.type) {
    case "CEILING": {
      const ceiling = docGetTableEntity(doc, handle);
      if (!ceiling) {
        return [];
      }

      const { isEditable } = isEditableCeilingOutline(ceiling);
      if (!isEditable) {
        return [];
      }

      return [
        {
          type: "SHAPE",
          parent: handle,
          shapeId: "outline",
        },
      ];
    }
    case "ROOF": {
      const roof = docGetTableEntity(doc, handle);
      if (roof && isVirtualRoofBlock(roof.blocks)) {
        return [
          {
            type: "SHAPE",
            parent: handle,
            shapeId: "outline",
          },
        ];
      }
      return [];
    }
    case "REFERENCE_GEOM": {
      return [
        {
          type: "SHAPE",
          parent: handle,
          shapeId: "geom",
        },
      ];
    }
    case "FLOOR_FINISH": {
      return [
        {
          type: "SHAPE",
          parent: handle,
          shapeId: "finishPerimeter",
        },
      ];
    }
    case "FLOOR": {
      const floor = docGetTableEntity(doc, handle);
      if (!floor || !isSolidFloorAssemblyOverride(floor) || !isHorizontalFloorAssemblyFreeForm(floor)) {
        return [];
      }

      const shapes: ShapeHandle[] = [];
      const perimeters = floor.perimeters ?? [];
      for (let i = 0; i < perimeters.length; i++) {
        shapes.push({
          type: "SHAPE",
          parent: handle,
          shapeId: `outline:${i}`,
        });
      }
      const openings = floor.openings ?? [];
      for (let i = 0; i < openings.length; i++) {
        shapes.push({
          type: "SHAPE",
          parent: handle,
          shapeId: `opening:${i}`,
        });
      }
      return shapes;
    }
    case "FOUNDATION": {
      return [
        {
          type: "SHAPE",
          parent: handle,
          shapeId: "outline",
        },
      ];
    }
    case "VIEWPORT":
    case "STAIR_BLOCK":
    case "ROOM": {
      return [
        {
          type: "SHAPE",
          parent: handle,
          shapeId: "outline",
        },
      ];
    }
    case "RAILING": {
      return [
        {
          type: "SHAPE",
          parent: handle,
          shapeId: "path",
        },
      ];
    }
    case "FREEFORM_WALL": {
      return [
        {
          type: "SHAPE",
          parent: handle,
          shapeId: "path",
        },
      ];
    }
    case "MEASUREMENT": {
      const entity = docGetTableEntity(doc, handle);

      if (!entity) {
        return [];
      }

      return Object.keys(entity.shapes).map((shapeId) => ({
        type: "SHAPE",
        parent: handle,
        shapeId,
      }));
    }
    case "SITE_SHAPE": {
      const entity = docGetTableEntity(doc, handle);
      if (!entity) {
        return [];
      }

      return [
        {
          type: "SHAPE",
          parent: handle,
          shapeId: "geom",
        },
      ];
    }
    case "ANNOTATION": {
      const annot = docGetTableEntity(doc, handle);

      if (annot?.type === AnnotationType.LINEWORK) {
        return [
          {
            type: "SHAPE",
            parent: handle,
            shapeId: "linework",
          },
        ];
      }

      break;
    }
    case "ROOM_LAYOUT_INSTANCE": {
      return [];
    }
    case "FLOOR_BAY":
    case "FLOOR_SPLIT_LINE": {
      return [];
    }
    case "EDIT_MODE": {
      return [];
    }
    default: {
      assertUnreachable(handle);
    }
  }

  return [];
}

export function docGetComponentHandlesFromShapeHandle(doc: Doc, handle: ShapeHandle): (EdgeHandle | VertexHandle)[] {
  const polyLine = docGetShape(doc, handle);

  if (!polyLine) {
    return [];
  }

  return [...edgeHandlesFromPolyLine2(handle, polyLine), ...vertexHandlesFromPolyLine2(handle, polyLine)];
}

export function vertexHandlesFromPolyLine2(parentShape: ShapeHandle, polyLine: PolyLine2): VertexHandle[] {
  const handles: VertexHandle[] = [];

  for (let i = 0; i < polyLine.length; i += 2) {
    handles.push({
      type: "VERTEX",
      parent: parentShape.parent,
      shapeId: parentShape.shapeId,
      vertexId: i / 2,
    });
  }

  return handles;
}

export function edgeHandlesFromPolyLine2(parentShape: ShapeHandle, polyLine: PolyLine2): EdgeHandle[] {
  const handles: EdgeHandle[] = [];

  for (let i = 0; i < polyLine.length - 2; i += 2) {
    handles.push({
      type: "EDGE",
      parent: parentShape.parent,
      shapeId: parentShape.shapeId,
      edgeId: i / 2,
    });
  }

  return handles;
}
